"use client";

import React, { useState } from "react";
import { withErrorBoundary } from "react-error-boundary";
import { Logger } from "lib/logger";
import { color } from "lib/theme/theme.css";
import type { LocaleOfStoreType } from "@beautypie/core";
import { changeRegion } from "./CountrySelector.utils";
import {
  countrySelector,
  countrySelectorWrapper,
  countrySelectorButton,
  countrySelectorTextWrapper,
  countrySelectorText,
  countrySelectorMenuList,
  countrySelectorMenuWrapper,
  countrySelectorMenuListOpen
} from "./CountrySelector.css";
import { ChevronDownIcon, UkFlagIcon, UsFlagIcon } from "@shared/ui.icons";

const countryIcons = {
  "en-GB": <UkFlagIcon width={30} height={30} />,
  "en-US": <UsFlagIcon width={30} height={30} />
};

type Item = {
  locale: LocaleOfStoreType;
  title: string;
};

const items: Item[] = [
  {
    locale: "en-GB",
    title: "United Kingdom (GBP)"
  },
  {
    locale: "en-US",
    title: "United States (USD)"
  }
];

type Props = {
  locale: LocaleOfStoreType;
};

const CountrySelectorComp: React.FC<Props> = ({ locale }) => {
  const selected = items.find((item) => item.locale === locale) ?? items[0];
  const menuItems = items.filter((item) => item.locale !== locale) ?? items[0];
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  const handleButtonClick = (menuItemLocale: LocaleOfStoreType) => {
    changeRegion(menuItemLocale);
  };

  return (
    <div className={countrySelector} data-testid="country-selector">
      <div className={countrySelectorWrapper}>
        <button
          type="button"
          data-testid="CountrySelectorButton"
          className={countrySelectorButton}
          onClick={() => setIsMenuOpen(!isMenuOpen)}
        >
          <div className={countrySelectorTextWrapper}>
            <p className={countrySelectorText}>
              {countryIcons[selected.locale]}
              {selected.title}
            </p>
            <ChevronDownIcon fill={color.contentTertiary} />
          </div>
        </button>

        <div
          className={`${countrySelectorMenuList} ${
            isMenuOpen ? countrySelectorMenuListOpen : ""
          }`}
        >
          <div>
            {menuItems.map((menuItem, index) => {
              const key = `MenuItem-${index}`;
              return (
                <button
                  type="button"
                  key={key}
                  data-testid={`${menuItem.locale}-selector`}
                  value={menuItem.locale}
                  onClick={() => {
                    handleButtonClick(menuItem.locale);
                    setIsMenuOpen(false);
                  }}
                >
                  <div className={countrySelectorMenuWrapper}>
                    {countryIcons[menuItem.locale]}
                    <p className={countrySelectorText}>{menuItem.title}</p>
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
};

export const CountrySelector = withErrorBoundary(CountrySelectorComp, {
  fallback: <div />,
  onError(error) {
    Logger.warn(`Country Selector failed to load. ${error}`);
  }
});
